/**
 * ADR (Architecture Decision Record) conflict detection.
 *
 * Scans scaffold files for ADR entries, detects explicit supersede relations
 * ("superseded by ADR-XXXX"), and makes them queryable so agents never act
 * on decisions that have been overridden.
 *
 * Deterministic — no AI, no network, pure Markdown parsing.
 */
import { readFileSync, existsSync } from "node:fs";
import { join } from "node:path";
import { globSync } from "glob";

export interface AdrEntry {
  /** Short identifier, e.g. "ADR-0003" */
  id: string;
  /** Full heading title */
  title: string;
  /** Source file (relative to project root) */
  file: string;
  /** Line number of the ADR heading */
  line: number;
  /** ID of the ADR that supersedes this one, if any */
  supersededBy: string | null;
  /** Date string from heading or frontmatter, if present */
  date: string | null;
}

/** Patterns that indicate an ADR has been replaced */
const SUPERSEDED_RE = /\bsuperseded\s+by\s+(ADR[-\s]?\d+)/i;
const ADR_HEADING_RE = /^#{1,4}\s+(ADR[-\s]?\d+[:\s].+)/i;
const ADR_ID_RE = /ADR[-\s]?(\d+)/i;
const DATE_RE = /\b(\d{4}-\d{2}-\d{2})\b/;

/**
 * Parse ADR entries from a markdown file's content.
 * Handles both:
 *   - Dedicated ADR files (one ADR per file)
 *   - decisions.md with multiple ## ADR-XXXX sections
 */
export function parseAdrEntries(content: string, relFile: string): AdrEntry[] {
  const entries: AdrEntry[] = [];
  const lines = content.split("\n");

  let currentAdr: AdrEntry | null = null;
  let bodyLines: string[] = [];

  const flush = () => {
    if (!currentAdr) return;
    // Scan body for supersede relation
    const body = bodyLines.join("\n");
    const supersededMatch = body.match(SUPERSEDED_RE);
    if (supersededMatch) {
      const raw = supersededMatch[1].replace(/\s+/, "-").toUpperCase();
      currentAdr.supersededBy = raw.startsWith("ADR-") ? raw : `ADR-${raw.split("-").pop()}`;
    }
    entries.push(currentAdr);
    currentAdr = null;
    bodyLines = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const headingMatch = line.match(ADR_HEADING_RE);

    if (headingMatch) {
      flush();
      const fullTitle = headingMatch[1].trim();
      const idMatch = fullTitle.match(ADR_ID_RE);
      const id = idMatch ? `ADR-${idMatch[1].padStart(4, "0")}` : fullTitle.slice(0, 20);
      const dateMatch = fullTitle.match(DATE_RE);
      currentAdr = {
        id,
        title: fullTitle,
        file: relFile,
        line: i + 1,
        supersededBy: null,
        date: dateMatch ? dateMatch[1] : null,
      };
    } else if (currentAdr) {
      bodyLines.push(line);
    }
  }
  flush();

  return entries;
}

/**
 * Scan all scaffold files for ADR entries and build an index.
 * Returns entries grouped by id.
 */
export function buildAdrIndex(
  scaffoldFiles: string[],
  projectRoot: string
): Map<string, AdrEntry> {
  const index = new Map<string, AdrEntry>();

  for (const absPath of scaffoldFiles) {
    let content: string;
    try { content = readFileSync(absPath, "utf8"); } catch { continue; }

    const relFile = absPath.startsWith(projectRoot)
      ? absPath.slice(projectRoot.length + 1)
      : absPath;

    // Only scan files that look like they contain ADRs
    if (!content.match(ADR_HEADING_RE) && !relFile.toLowerCase().includes("decision")) continue;

    const entries = parseAdrEntries(content, relFile);
    for (const e of entries) {
      // Last write wins for duplicate IDs (shouldn't happen in practice)
      index.set(e.id, e);
    }
  }

  return index;
}

/**
 * Given a query about an ADR (by id or topic), resolve supersede chains.
 * Returns a formatted response ready for injection into cai_search output.
 */
export function resolveAdrStatus(
  query: string,
  index: Map<string, AdrEntry>
): string | null {
  if (index.size === 0) return null;

  const queryUpper = query.toUpperCase();

  // Find matching ADRs by id or partial title match
  const matches: AdrEntry[] = [];
  for (const entry of index.values()) {
    if (
      entry.id.toUpperCase().includes(queryUpper) ||
      entry.title.toLowerCase().includes(query.toLowerCase())
    ) {
      matches.push(entry);
    }
  }

  if (matches.length === 0) return null;

  const lines: string[] = [];
  for (const adr of matches) {
    if (adr.supersededBy) {
      const successor = index.get(adr.supersededBy);
      const successorLabel = successor
        ? `${successor.id}: ${successor.title}`
        : adr.supersededBy;
      lines.push(`**${adr.id}** [SUPERSEDED by ${successorLabel}]`);
      lines.push(`  Original: ${adr.title} (${adr.file}:${adr.line})`);
      if (successor) {
        lines.push(`  Current decision: ${successor.title} (${successor.file}:${successor.line})`);
      }
    } else {
      lines.push(`**${adr.id}** [ACTIVE] ${adr.title}`);
      lines.push(`  Source: ${adr.file}:${adr.line}`);
    }
  }

  return lines.join("\n");
}

/**
 * Find all ADR files in a project (dedicated /adr/, /docs/decisions/, or scaffold files).
 */
export function findAdrFiles(projectRoot: string, scaffoldRoot: string): string[] {
  const candidates: string[] = [];

  // Dedicated ADR directories
  for (const dir of ["adr", "docs/decisions", "docs/adr", ".decisions"]) {
    const abs = join(projectRoot, dir);
    if (existsSync(abs)) {
      candidates.push(...globSync("*.md", { cwd: abs, absolute: true }));
    }
  }

  // Scaffold decisions.md files
  const decisionsFiles = globSync("**/decisions*.md", {
    cwd: scaffoldRoot,
    absolute: true,
    ignore: ["**/node_modules/**"],
  });
  candidates.push(...decisionsFiles);

  return [...new Set(candidates)];
}
